// @ts-nocheck
import * as React from 'react';
import times from 'lodash.times';
import { Link } from 'react-router-dom';
import { getTheme } from 'settings/theme/index';
import { images } from 'settings/';
import Icon from 'components/uielements/Icon/index';
import defaultSchoolImage from 'assets/images/school-placeholder.png';
import defaultGroupImage from 'assets/images/group-placeholder.png';
import ProgramTypeSvg from 'assets/svg/program_type.svg';
import { formattedSchoolName } from 'utils/string/index';
import { HighlightCardWrapper, StyledMaterialCard } from './HighlightCard.style';
import type { HighlightCardProps } from './HighlightCard.types';

const {
  palette: { noodle, blue, primary },
} = getTheme();
const REACT_APP_CLOUDFRONT_URL = process.env.REACT_APP_CLOUDFRONT_URL || '';

// TODO: This is going to be used in SearchResultsList.js which is ugly as f.
// This component can be improved by further decoupling the data
// from the "view" but time constraints!
const HighlightCard = ({ itemData, contentType }: HighlightCardProps): React.ReactElement<any> => {
  const getImagePath = () => {
    const image = itemData.image || (itemData.images && itemData.images.avatar) || '';
    let fullImage = image;

    if (!(fullImage.startsWith('//') || fullImage.startsWith('http'))) {
      fullImage = `${REACT_APP_CLOUDFRONT_URL}${image}`;
    }

    switch (contentType) {
      case 'group':
        return fullImage || defaultGroupImage;

      case 'school':
        return itemData.avatar || defaultSchoolImage;

      case 'expert':
        return itemData.avatar || images.defaults.avatar.article;

      case 'counselor':
        return itemData.avatar || images.defaults.avatar.user;

      case 'area_of_interest':
        return itemData.icon || images.defaults.avatar.article;

      case 'program_type':
        return ProgramTypeSvg || images.defaults.avatar.article;

      case 'course':
        return itemData.card_image;

      case 'partner_programs':
        return itemData.cover_image;

      case 'team':
        return itemData.image;

      default:
        return images.defaults.avatar.article;
    }
  };

  const getCardBorderColor = (): string => {
    switch (contentType) {
      case 'school':
        return noodle.primary;

      case 'area_of_interest':
        return blue.gigas;

      case 'program_type':
        return blue.shipCove;

      case 'sponsored':
        return blue.shipCove;

      case 'counselor':
        return noodle.secondary;

      case 'partner_programs':
        return primary.sixty;

      case 'course':
        return primary.hundred;

      default:
        return '';
    }
  };

  const getLinkToPage = (): string => {
    switch (contentType) {
      case 'school':
        return `/school/I${itemData.id.split('-')[0]}-${formattedSchoolName(
          itemData.name,
        )}/details`;

      case 'sponsored':
        return '';

      case 'counselor':
        return '';

      case 'area_of_interest':
        return `/interests/${itemData.slug}`;

      case 'program_type':
        return `/programs/${itemData.slug}`;

      case 'course':
        return `/courses/${itemData.slug}`;

      case 'team':
        return `/teams/preview/${itemData.id}`;

      case 'partner_programs':
        return itemData.url;

      default:
        return '';
    }
  };

  const renderDollarSigns = (cost: number): React.ReactElement<any> => (
    <>
      {times(cost, (idx) => (
        <Icon key={`green-dollar-icon-${idx}`} icon='icon-dollar' />
      ))}
      {times(5 - cost, (idx) => (
        <Icon key={`dollar-icon-${idx}`} icon='icon-dollar icon-dollar--grey' />
      ))}
    </>
  );

  const renderCardContent = () => {
    switch (contentType) {
      case 'program':
        return (
          <div className='highlight-card__content__container'>
            <h4 className='highlight-card__title'>{itemData.title}</h4>
          </div>
        );

      case 'partner_programs':
        return (
          <div className='highlight-card__content__container'>
            <h4 className='highlight-card__subheading'>{itemData.university_name}</h4>
            <h5 className='highlight-card__title'>{itemData.program_name}</h5>
          </div>
        );

      case 'school':
        return (
          <div className='highlight-card__content__wrapper'>
            <div className='highlight-card__content__container'>
              <h4 className='highlight-card__title'>{itemData.name}</h4>
              <div className='highlight-card__school__rating'>
                <div>
                  <span className='highlight-card__school__indicator'>Out of State</span>
                  <div className='icon-dollar__container'>
                    {renderDollarSigns(itemData.undergrad_out_of_state_costs[0]?.rank)}
                  </div>
                </div>
                <div>
                  <span className='highlight-card__school__indicator'>In State</span>
                  <div className='icon-dollar__container'>
                    {renderDollarSigns(itemData.undergrad_in_state_costs[0]?.rank)}
                  </div>
                </div>
                <div>
                  <span className='highlight-card__school__indicator'>Accept</span>
                  <span className='highlight-card__school__acceptance'>
                    {itemData.acceptance_percentage ? `${itemData.acceptance_percentage}%` : 'N/A'}
                  </span>
                </div>
                <div />
              </div>
            </div>
          </div>
        );

      case 'area_of_interest':
      case 'program_type':
        return (
          <div className='highlight-card__content__container'>
            <h4 className='highlight-card__title'>{itemData.title}</h4>
            <h5 className='highlight-card__subheading'>
              {contentType.replace(/_/g, ' ').toUpperCase()}
            </h5>
          </div>
        );

      case 'counselor':
        return (
          <div className='highlight-card__content__wrapper'>
            <div className='highlight-card__content__container'>
              <span className='highlight-card__regular-text'>Speak with {itemData.name}.</span>
              {/* <Link to='#'>
               <span className='highlight-card__regular-text'>
                 Schedule a counseling call
               </span>
              </Link> */}
            </div>
          </div>
        );

      case 'team':
        return (
          <div className='highlight-card__content__container'>
            <h4 className='highlight-card__title'>{itemData.name}</h4>
            <h5 className='highlight-card__subheading'>
              {contentType.replace(/_/g, ' ').toUpperCase()}
            </h5>
          </div>
        );

      case 'course':
        return (
          <div className='highlight-card__content__container'>
            <h4 className='highlight-card__title'>{itemData.short_title}</h4>
            <h5 className='highlight-card__subheading'>
              {contentType.replace(/_/g, ' ').toUpperCase()}
            </h5>
          </div>
        );

      default:
        return '';
    }
  };

  const renderAvatar = (): React.ReactElement<any> => {
    if (contentType === 'area_of_interest') {
      return (
        <object
          className='highlight-card__object'
          style={{
            opacity: 0.66,
          }}
          data={getImagePath()}
          type='image/svg+xml'
        >
          <p>Program Image</p>
        </object>
      );
    }

    if (contentType === 'program_type') {
      return (
        <object
          className='highlight-card__object'
          style={{
            opacity: 0.33,
          }}
          data={getImagePath()}
          type='image/svg+xml'
        >
          <p>Program Image</p>
        </object>
      );
    }

    return (
      <img
        className='highlight-card__image'
        src={getImagePath()}
        alt=''
        loading='lazy'
        width='100%'
        height='100%'
      />
    );
  };

  const linkTarget = contentType === 'partner_programs' ? '_blank' : '_self';
  return (
    <HighlightCardWrapper>
      <Link
        to={{
          pathname: getLinkToPage(),
        }}
        target={linkTarget}
      >
        <StyledMaterialCard borderColor={getCardBorderColor()}>
          {renderAvatar()}
          {renderCardContent()}
        </StyledMaterialCard>
      </Link>
    </HighlightCardWrapper>
  );
};

export default HighlightCard;
